import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Document, Page } from 'react-pdf';
import { Box, CircularProgress } from '@mui/material';
import { isImageFile } from '../../../utils/util.js';
import DisplaySign from '../../Pages/CKEditorComponent/CKEditorSidebar/display-sign.js';

const PageRenderer = ({
	isEsignFlow,
	combinedDoc,
	pages,
	totalPages,
	pageRefs,
	onDocumentLoadSuccess,
	showSign = false,
	file
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [renderedStates, setRenderedStates] = useState(
		Array.from({ length: pages.length }).fill(false)
	);

	const handlePageRender = (index) => {
		setRenderedStates((prev) => {
			const updated = [...prev];
			updated[index] = true;
			return updated;
		});
	};

	const allPagesRendered = renderedStates.every(Boolean);

	if (allPagesRendered && isLoading) {
		setIsLoading(false);
	}

	if (isEsignFlow && combinedDoc) {
		return isImageFile(combinedDoc) ? (
			<img src={combinedDoc} alt='Document' style={{ width: '100%' }} />
		) : (
			<Document file={combinedDoc} onLoadSuccess={onDocumentLoadSuccess}>
				{Array.from({ length: totalPages }, (el, index) => (
					<div
						key={'esign-' + index}
						ref={(el) => {
							pageRefs.current[index] = el;
						}}
						style={{ backgroundColor: 'black', position: 'relative' }}
					>
						{!renderedStates[index] && (
							<Box
								sx={{
									minHeight: '75vh',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									background: 'black',
									position: 'absolute',
									width: '100%',
									zIndex: 10
								}}
							>
								<div style={{ textAlign: 'center' }}>
									<CircularProgress />
									<p style={{ color: 'white' }}>Loading page...</p>
								</div>
							</Box>
						)}

						<div
							style={{
								visibility: renderedStates[index] ? 'visible' : 'hidden',
								width: 'fit-content'
							}}
						>
							<Page
								pageNumber={index + 1}
								renderTextLayer={false}
								renderAnnotationLayer={false}
								onRenderSuccess={() => handlePageRender(index)}
							/>
							{showSign
								&& file?.recipientData?.map((item) =>
									item.coordinates
										?.filter((item) => item.pageNo === index + 1)
										?.map((coord, coordIndex) => (
											<div
												className='approval-pdf-sign'
												key={'coord' + coordIndex}
												style={{
													position: 'absolute',
													top: Number(coord.y),
													left: Number(coord.x),
													userSelect: 'none'
												}}
											>
												<DisplaySign
													Rname={[
														item.recipientFirstName,
														item.recipientMiddleName,
														item.recipientLastName
													]
														.filter(Boolean)
														.join(' ')}
												/>
											</div>
										))
								)}

							{index < totalPages - 1 && (
								<hr style={{ borderTop: '1px solid #000', margin: '20px 0' }} />
							)}
						</div>
					</div>
				))}
			</Document>
		);
	}

	// Non-esign flow
	return pages.map((pageUrl, index) => (
		<div
			key={'page-' + index}
			ref={(el) => {
				pageRefs.current[index] = el;
			}}
			style={{ backgroundColor: 'black', position: 'relative' }}
		>
			{isImageFile(pageUrl) ? (
				<img
					src={pageUrl}
					alt={`Page ${index + 1}`}
					style={{ width: '100%' }}
				/>
			) : (
				<>
					{!renderedStates[index] && (
						<Box
							sx={{
								minHeight: '75vh',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								background: 'black',
								position: 'absolute',
								width: '100%',
								zIndex: 10
							}}
						>
							<div style={{ textAlign: 'center' }}>
								<CircularProgress />
								<p style={{ color: 'white' }}>Loading document...</p>
							</div>
						</Box>
					)}
					<div
						style={{ visibility: renderedStates[index] ? 'visible' : 'hidden' }}
					>
						<Document file={pageUrl}>
							<Page
								pageNumber={1}
								renderTextLayer={false}
								renderAnnotationLayer={false}
								onRenderSuccess={() => handlePageRender(index)}
							/>
						</Document>
					</div>
				</>
			)}
			{index < pages.length - 1 && (
				<hr style={{ borderTop: '1px solid #000', margin: '20px 0' }} />
			)}
		</div>
	));
};

PageRenderer.propTypes = {
	isEsignFlow: PropTypes.bool,
	combinedDoc: PropTypes.string,
	pages: PropTypes.array,
	totalPages: PropTypes.number,
	pageRefs: PropTypes.object,
	onDocumentLoadSuccess: PropTypes.func,
	showSign: PropTypes.bool,
	file: PropTypes.object
};

export default PageRenderer;
